import React, { useEffect, useState } from 'react'
import API from '../../services/api.js';
import LoadingPopup from '../misc/Loading.js';

export default function Settings() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [popup, setPopup] = useState({ show: false, message: '', type: '' })

  useEffect(() => {
    const controller = new AbortController();
    fetchUsers()
    return () => controller.abort(); // Cleanup on unmount
  }, [])

  // Show popup with auto-hide
  const showPopup = (message, type) => {
    setPopup({ show: true, message, type })
    setTimeout(() => {
      setPopup({ show: false, message: '', type: '' })
    }, 3000)
  }

  async function fetchUsers() {
    setLoading(true)
    try {
      const response = await API.get(`${process.env.REACT_APP_DJANGO_API_URL}/get-tenant-users/`);
      setUsers(response.data.users)
    } catch (error) {
      console.error("Error fetching users:", error)
    }
    setLoading(false)
  }

  async function deleteUser(id, role) {
    console.log("Deleting user with ID:", id, "and role:", role)

    if (role === "viewer") {
      try {
        const response = await API.post(`${process.env.REACT_APP_DJANGO_API_URL}/delete-tenant-user/`, { user_id: id, role: role });
        if (response.data.status === "success") {
          showPopup("User successfully deleted", "success")
          // Refresh the table by fetching users again
          await fetchUsers()
        } else {
          showPopup(response.data.message, "error")
        }
      } catch (error) {
        console.error("Error deleting user:", error)
        showPopup("Deletion operation failed", "error")
      }
    } else {
      showPopup("User role is not Viewer", "error")
    }
  }

  return (
    <div className="p-6 sm:p-8 text-white min-h-screen relative">

      {/* Toast notification */}
      {popup.show && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-xl border transition-all duration-300 ease-in-out transform ${
          popup.show ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
        } ${
          popup.type === 'success'
            ? 'bg-green-500/10 border-green-500/20 text-green-400'
            : 'bg-red-500/10 border-red-500/20 text-red-400'
        }`}>
          <div className="flex items-center gap-2">
            {popup.type === 'success' ? (
              <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
            <span className="font-medium text-sm">{popup.message}</span>
          </div>
        </div>
      )}

      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-white font-bold text-3xl">User Management</h1>
        <p className="text-gray-400 text-sm mt-1">Manage team members and their access levels</p>
      </div>

      {/* Team Members section card */}
      <div className="bg-[#101012] border border-[#232325] rounded-2xl overflow-hidden">

        {/* Card header — section divider */}
        <div className="px-6 py-4 border-b border-[#232325] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p className="text-white font-semibold text-lg">Team Members</p>
            <p className="text-gray-400 text-sm mt-0.5">All users with access to this workspace</p>
          </div>
          {!loading && users.length > 0 && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 self-start sm:self-auto">
              {users.length} {users.length === 1 ? 'member' : 'members'}
            </span>
          )}
        </div>

        {loading ? (
          <div className="px-6 py-12 flex justify-center">
            <LoadingPopup isVisible={true}/>
          </div>
        ) : users.length === 0 ? (
          <div className="px-6 py-12 flex flex-col items-center justify-center text-center">
            <svg className="w-10 h-10 text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-gray-500 text-sm">No users found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-white min-w-[560px]">
              <thead>
                <tr className="bg-[#232325] text-left">
                  <th className="px-4 py-3 text-gray-400 text-sm font-medium text-center whitespace-nowrap">S.No</th>
                  <th className="px-4 py-3 text-gray-400 text-sm font-medium text-center whitespace-nowrap">Email</th>
                  <th className="px-4 py-3 text-gray-400 text-sm font-medium text-center whitespace-nowrap">ID</th>
                  <th className="px-4 py-3 text-gray-400 text-sm font-medium text-center whitespace-nowrap">Role</th>
                  <th className="px-4 py-3 text-gray-400 text-sm font-medium text-center whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={user.id} className="border-t border-[#232325] hover:bg-[#1a1a1c] transition-colors">
                    <td className="px-4 py-3 text-gray-400 text-sm text-center align-middle">{index + 1}</td>
                    <td className="px-4 py-3 text-sm text-center align-middle whitespace-nowrap">{user.email}</td>
                    <td className="px-4 py-3 text-gray-400 text-sm text-center align-middle whitespace-nowrap">{user.user_id}</td>
                    <td className="px-4 py-3 text-center align-middle">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'client_admin'
                          ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                          : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                      }`}>
                        {user.role === 'viewer' ? 'Viewer' : user.role === 'client_admin' ? 'Administrator' : user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center align-middle">
                      {user.role === 'viewer' && (
                        <button
                          className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                          onClick={() => deleteUser(user.user_id, user.role)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
